/**
 * Parsers for `validateSearch` in TanStack Router route definitions. They keep
 * `undefined` distinct from an empty/false value so routes can tell "param
 * absent" from "param explicitly cleared" — some filters rely on that to pick
 * a default.
 */

/** Parses a raw search value into a string array, preserving `undefined`. */
export const stringArray = <T extends string>(raw: unknown): T[] | undefined => {
  if (!Array.isArray(raw)) {
    return
  }
  return raw.filter((item): item is T => typeof item === 'string')
}

/** Like `stringArray`, but drops members outside `allowed`. */
export const enumArray = <T extends string>(raw: unknown, allowed: readonly T[]): T[] | undefined =>
  stringArray<T>(raw)?.filter((item) => allowed.includes(item))

/** Narrows a raw search value to a branded string type (e.g. a Convex id). */
const isString = <T extends string>(raw: unknown): raw is T => typeof raw === 'string'

/** Returns the string when non-empty; `undefined` otherwise. */
export const optionalString = <T extends string = string>(raw: unknown): T | undefined => {
  if (!isString<T>(raw) || raw === '') {
    return
  }
  return raw
}

/** Returns `true` only for `true` / `'true'`; `undefined` otherwise. */
export const optionalBoolean = (raw: unknown): boolean | undefined => {
  if (raw === true || raw === 'true') {
    return true
  }
}
